import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { SkyNavDrawer } from "@skypointcloud/skypoint-ui";
import { useMsalAuth } from "../../../hooks/useMsalAuth";
import { buildNavSections, type NavId } from "./sidebarConfig";
import { getNavRoute, getSelectedNavItem } from "./routeMapping";
import { IRootReducerState } from "../../../store/IRootReducer";
import { useGetMyPermissionsQuery } from "../../../store/api/rbacApi";
import { useGetMyPortalsQuery } from "../../../store/api/portalsApi";

interface SidebarSkyProps {
  collapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

const MOBILE_BREAKPOINT = 992;

const SidebarSky = ({ collapsed, onCollapseChange }: SidebarSkyProps) => {
  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const portalView = useSelector((state: IRootReducerState) => state.uiReducer.portalView);
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = authState.permissions;
  const isFullAccess = authState.isFullAccess;
  const { getQueryString } = useMsalAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch RBAC permissions for current user
  const { data: rbacPermissions } = useGetMyPermissionsQuery();

  // Fetch portals for sidebar flyout
  const { data: portalsData } = useGetMyPortalsQuery();

  // When admin switches to user portal view, override nav to show user portal sections
  const isAdminViewingAsUser =
    (isPlatformAdmin ||
      isTenantAdmin ||
      rbacPermissions?.isAdmin ||
      rbacPermissions?.isB2CAdmin ||
      rbacPermissions?.isManager) &&
    portalView === "user";

  // Build nav sections based on permissions (admin flags + RBAC navigation)
  // When viewing as user, pass restricted navigation flags to trigger user portal nav
  const portalsList = portalsData?.map((p) => ({ key: p.key, name: p.name }));

  const sections = isAdminViewingAsUser
    ? buildNavSections({
        isFullAccess: false,
        isTenantAdmin: false,
        isPlatformAdmin: false,
        isInstanceAdmin: false,
        rbacIsAdmin: false,
        rbacNavigation: {
          showSupportTickets: true,
          showSettings: false,
          showTeam: false,
          showSupportWorkspace: false,
        },
      })
    : buildNavSections({
        isFullAccess,
        isTenantAdmin,
        isPlatformAdmin,
        isInstanceAdmin,
        rbacIsAdmin:
          rbacPermissions?.isAdmin || rbacPermissions?.isB2CAdmin || rbacPermissions?.isManager,
        rbacNavigation: rbacPermissions?.navigation,
        portals: portalsList,
      });

  // Collect nav items that have children (flyout/accordion) — these
  // should NOT navigate when selected; they only toggle their submenu.
  const { parentIds, parentLabels } = useMemo(() => {
    const ids = new Set<string>();
    const labels = new Set<string>();
    for (const section of sections) {
      for (const item of section.items) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((item as any).children?.length) {
          ids.add(item.id);
          labels.add(item.label);
        }
      }
    }
    return { parentIds: ids, parentLabels: labels };
  }, [sections]);

  // Get current selection based on route
  const queryString = getQueryString();
  const { selectedId, selectedCategoryId } = getSelectedNavItem(location.pathname, queryString);

  // Handle nav item selection — skip navigation for parent items with children
  const handleNavSelect = (itemId: string) => {
    if (parentIds.has(itemId)) return; // has flyout, don't navigate

    let route: string | undefined;

    // Handle dynamic portal child items
    if (itemId === "portal-all") {
      route = "/portals";
    } else if (itemId === "portal-create") {
      route = "/portals/new";
    } else if (itemId.startsWith("portal-")) {
      const portalKey = itemId.replace("portal-", "");
      route = `/portals/${portalKey}/list`;
    } else {
      route = getNavRoute(itemId as NavId);
    }

    if (route) {
      navigate(route + queryString);
      // Close mobile overlay after navigation
      if (window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches) {
        onCollapseChange?.(true);
      }
    }
  };

  // ── Portal separator & action-item icons ─────────────────────────────
  // The NavItem type from skypoint-ui doesn't support dividers or icons
  // on child items, so we insert them via the DOM.  A MutationObserver
  // ensures it works for both expanded-sidebar children AND collapsed-
  // flyout popovers (rendered in a React Portal).
  const portalCount = portalsData?.length ?? 0;

  const ICON_GAP = "6px";
  const ICON_SIZE = "17";
  const EYE_SVG = `<svg fill="currentColor" aria-hidden="true" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0"><path d="M3.26 11.6A6.97 6.97 0 0 1 10 6c3.2 0 6.06 2.33 6.74 5.6a.5.5 0 0 0 .98-.2A7.97 7.97 0 0 0 10 5a7.97 7.97 0 0 0-7.72 6.4.5.5 0 0 0 .98.2ZM10 8a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm-2.5 3.5a2.5 2.5 0 1 1 5 0 2.5 2.5 0 0 1-5 0Z" fill="currentColor"></path></svg>`;
  const PLUS_SVG = `<svg fill="currentColor" aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0"><path d="M10 2.5c.28 0 .5.22.5.5v6.5H17a.5.5 0 0 1 0 1h-6.5V17a.5.5 0 0 1-1 0v-6.5H3a.5.5 0 0 1 0-1h6.5V3c0-.28.22-.5.5-.5Z" fill="currentColor"></path></svg>`;

  useEffect(() => {
    const SEPARATOR_ATTR = "data-portal-separator";
    const ICON_ATTR = "data-portal-icon";

    const enhance = () => {
      document.querySelectorAll<HTMLButtonElement>("button").forEach((btn) => {
        const text = btn.textContent?.trim();

        // ── "View All Portals" — separator + eye icon ──
        if (text === "View All Portals") {
          // Insert separator if portals exist above
          if (portalCount > 0) {
            const prev = btn.previousElementSibling;
            if (prev && prev.tagName === "BUTTON" && !prev.hasAttribute(SEPARATOR_ATTR)) {
              const sep = document.createElement("div");
              sep.setAttribute(SEPARATOR_ATTR, "");
              sep.setAttribute("aria-hidden", "true");
              sep.className = "border-t border-[var(--colorNeutralStroke2)] my-1";
              btn.parentElement!.insertBefore(sep, btn);
            }
          }

          // Inject eye icon
          if (!btn.querySelector(`[${ICON_ATTR}]`)) {
            const wrapper = document.createElement("span");
            wrapper.setAttribute(ICON_ATTR, "");
            wrapper.style.display = "inline-flex";
            wrapper.style.alignItems = "center";
            wrapper.style.marginRight = ICON_GAP;
            wrapper.innerHTML = EYE_SVG;
            btn.prepend(wrapper);
          }
        }

        // ── "Create a Portal" — plus icon, same pattern as eye ──
        if (text === "Create a Portal" && !btn.querySelector(`[${ICON_ATTR}]`)) {
          const wrapper = document.createElement("span");
          wrapper.setAttribute(ICON_ATTR, "");
          wrapper.style.display = "inline-flex";
          wrapper.style.alignItems = "center";
          wrapper.style.marginRight = ICON_GAP;
          wrapper.innerHTML = PLUS_SVG;
          btn.prepend(wrapper);
        }
      });
    };

    // Run once immediately, then watch for DOM changes (flyout popovers)
    enhance();
    const observer = new MutationObserver(enhance);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [portalCount, sections]);

  // ── Hover-to-open for collapsed popover flyouts ──────────────────────
  // The library uses Radix Popover (click-triggered). We attach mouseenter
  // directly to each trigger button and use a grace timer so the user can
  // move their cursor from the trigger into the portal-rendered popover.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!collapsed) return;

    // Find the actual .sky-nav element in the DOM (not behind display:contents)
    const skyNav = document.querySelector<HTMLElement>(".sky-nav");
    if (!skyNav) return;

    const cancelClose = () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };

    /**
     * The open flyout, found among the triggers we attached to and nowhere
     * else.
     *
     * This used to be `skyNav.querySelector("button[data-state='open']")`,
     * which matches any button in the drawer carrying that attribute — and
     * clicking a plain nav item navigates. On the skyBrain settings page the
     * selected item carried it, so moving the mouse across the rail clicked
     * it 120ms later and the app navigated to skyBrain settings on its own.
     */
    const openTrigger = () => triggers.find((b) => b.dataset.state === "open") ?? null;

    const scheduleClose = () => {
      cancelClose();
      closeTimer.current = setTimeout(() => {
        openTrigger()?.click();
      }, 120);
    };

    /**
     * The collapsed flyout triggers.
     *
     * Two things this must get right, because everything below CLICKS what
     * it finds and a click on a plain nav item navigates.
     *
     * `querySelectorAll`, not `querySelector`: labels are not unique. Both
     * skyBrain's "Settings" leaf and the top-level "Settings" parent render
     * a button with that aria-label, and the leaf comes first in the DOM, so
     * taking the first match attached flyout behaviour to an ordinary link.
     *
     * And `aria-haspopup` must be present. `data-state` is not enough to tell
     * them apart: the library puts it on the SELECTED item too, so on the
     * skyBrain settings page the selected "Settings" leaf carried
     * `data-state="open"` — which is precisely how leaving it clicked it and
     * navigated to the page you were already on. Only something that opens a
     * popup announces `aria-haspopup`.
     */
    const triggers: HTMLButtonElement[] = [];
    parentLabels.forEach((label) => {
      skyNav.querySelectorAll<HTMLButtonElement>(`button[aria-label="${label}"]`).forEach((btn) => {
        if (btn.hasAttribute("aria-haspopup")) triggers.push(btn);
      });
    });

    const onEnter = (e: Event) => {
      cancelClose();
      const btn = e.currentTarget as HTMLButtonElement;
      if (btn.dataset.state === "closed") {
        // Close any already-open popover first — again only among triggers.
        const open = openTrigger();
        if (open && open !== btn) open.click();
        btn.click();
      }
    };

    const onLeave = (e: Event) => {
      const me = e as MouseEvent;
      const related = me.relatedTarget as HTMLElement | null;
      if (related?.closest?.("[data-radix-popper-content-wrapper]")) {
        cancelClose();
        return;
      }
      scheduleClose();
    };

    triggers.forEach((btn) => {
      btn.addEventListener("mouseenter", onEnter);
      btn.addEventListener("mouseleave", onLeave);
    });

    // Track portal-rendered popover content so hovering it keeps it open
    const attachedPopovers = new WeakSet<HTMLElement>();
    const onPopoverEnter = () => cancelClose();
    const onPopoverLeave = (e: Event) => {
      const me = e as MouseEvent;
      const related = me.relatedTarget as HTMLElement | null;
      if (related?.closest?.(".sky-nav")) return;
      scheduleClose();
    };

    const attachPopoverListeners = () => {
      document
        .querySelectorAll<HTMLElement>("[data-radix-popper-content-wrapper]")
        .forEach((el) => {
          if (attachedPopovers.has(el)) return;
          attachedPopovers.add(el);
          el.addEventListener("mouseenter", onPopoverEnter);
          el.addEventListener("mouseleave", onPopoverLeave);
        });
    };

    const observer = new MutationObserver(attachPopoverListeners);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelClose();
      triggers.forEach((btn) => {
        btn.removeEventListener("mouseenter", onEnter);
        btn.removeEventListener("mouseleave", onLeave);
      });
      observer.disconnect();
    };
  }, [collapsed, sections, parentLabels]);

  return (
    <SkyNavDrawer
      className="sky-nav"
      sections={sections}
      selectedId={selectedId}
      selectedCategoryId={selectedCategoryId}
      onSelect={handleNavSelect}
      size="small"
      collapsible
      onCollapseChange={onCollapseChange}
      showCopyright
      {...(collapsed !== undefined ? { collapsed } : {})}
    />
  );
};

export default SidebarSky;
